import type * as React from "react";
import { toast as sonner } from "sonner";

/**
 * API heredada de shadcn (`toast({ title, description, variant })`) sobre el único toaster del
 * admin, Sonner (`components/ui/sonner.tsx`). Antes había dos toasters con posición, duración y
 * aspecto distintos; ahora todo aviso sale igual: abajo a la derecha, máximo 5 s, con X, se
 * desliza para quitarlo y se apilan si llegan varios.
 *
 * `variant: "destructive"` es un error; sin variante, un aviso de éxito (así se usaba).
 */
type Toast = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: "default" | "destructive" | "success" | null;
  duration?: number;
  className?: string;
};

function toast({ title, description, variant, duration, className }: Toast) {
  const opciones = { description, duration, className };
  // Sin título, el texto de la descripción hace de mensaje principal.
  const mensaje = title ?? description ?? "";
  const resto = title ? opciones : { ...opciones, description: undefined };
  const id = variant === "destructive" ? sonner.error(mensaje, resto) : sonner.success(mensaje, resto);
  return {
    id: String(id),
    dismiss: () => sonner.dismiss(id),
    update: (t: Toast) => {
      const sig = t.title ?? t.description ?? mensaje;
      const op = { id, description: t.title ? t.description : undefined, duration: t.duration };
      if ((t.variant ?? variant) === "destructive") sonner.error(sig, op);
      else sonner.success(sig, op);
    },
  };
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string) => sonner.dismiss(toastId),
  };
}

export { useToast, toast };
